'use client';

import { useRef } from 'react';

import { useGSAP } from '@gsap/react';
import { Typography, TypographyProps } from '@mui/material';
import clsx from 'clsx';
import { gsap } from 'gsap';

import { loadSplitText } from '@/utils/gsap/lazyPlugins';

import styles from './AnimatedTitle.module.scss';

interface AnimatedTitleProps {
  title: string;
  variant?: TypographyProps['variant'];
  component?: TypographyProps['component'];
  className?: string;
}

const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false;

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const AnimatedTitle = ({ title, variant = 'h1', component = 'h1', className }: AnimatedTitleProps) => {
  const titleRef = useRef<HTMLElement>(null);

  useGSAP(() => {
    if (!titleRef.current) {
      return () => {};
    }

    if (prefersReducedMotion()) {
      gsap.set(titleRef.current, { opacity: 1 });

      return () => {};
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let childSplit: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let parentSplit: any = null;
    let isCancelled = false;

    const initAnimation = async () => {
      if (isCancelled || !titleRef.current) return;

      const SplitText = await loadSplitText();

      if (isCancelled || !titleRef.current) return;

      childSplit = new SplitText(titleRef.current, {
        type: 'lines',
        linesClass: 'lineChild',
      });

      parentSplit = new SplitText(titleRef.current, {
        type: 'lines',
        linesClass: 'lineParent',
      });

      gsap.set(childSplit.lines, { opacity: 0, x: -50 });
      gsap.set(titleRef.current, { opacity: 1 });

      gsap.to(childSplit.lines, {
        duration: 1,
        opacity: 1,
        x: 0,
        stagger: 0.25,
        delay: 0.2,
        ease: 'expo.out',
        force3D: true,
        immediateRender: false,
      });
    };

    const startAnimation = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            if (document.fonts && document.fonts.ready) {
              document.fonts.ready.then(initAnimation);
            } else {
              initAnimation();
            }
          },
          { timeout: 2000 }
        );
      } else {
        setTimeout(() => {
          if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(initAnimation);
          } else {
            initAnimation();
          }
        }, 100);
      }
    };

    startAnimation();

    return () => {
      isCancelled = true;
      childSplit?.revert();
      parentSplit?.revert();
    };
  }, [title]);

  return (
    <Typography
      ref={titleRef}
      variant={variant}
      component={component}
      classes={{ root: styles.root }}
      className={clsx(styles.title, className)}
    >
      {title}
    </Typography>
  );
};

export default AnimatedTitle;
